import {GoogleMap, useJsApiLoader, Marker, InfoWindow} from '@react-google-maps/api';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {User, Location, Visit, Session, Stay} from "../types/Types";
import {collection, getDocs, query, where} from "@firebase/firestore";
import {db, functions} from "../../firebase/FirebaseProvider";
import {Link} from "react-router-dom";
import {Button, ButtonGroup} from "react-bootstrap";
import {httpsCallable} from "@firebase/functions";
import {getGoogleMapsLink, logDataLoaded} from "../util/Util";
import PageTracking from "../auth/PageTracking";
import AuthCheck from '../auth/AuthCheck';
import {usePreventMobileZoom} from '../../hooks/usePreventMobileZoom';
import {GOOGLE_MAPS_API_KEY} from '../../config/googleMaps';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBed, faCheckCircle, faLayerGroup, faLocationDot, faSatelliteDish} from "@fortawesome/free-solid-svg-icons";
import "./Map.scss";

const containerStyle = {
    width: "100%",
    height: "67vh"
};

const center = {
    lat: 54.8,
    lng: -3.0
};

interface Props {
    locations: Location[];
    sessions: Session[];
    currentUser: User | undefined;
}

type MapViewMode = "visits" | "checkIns" | "stays";

const MapElement: React.FC<Props> = (props) => {
    const [locationsMap, setLocationsMap] = useState<Map<string, Location>>(new Map());
    const [visitsMap, setVisitsMap] = useState<Map<string, Visit>>(new Map());
    const [staysMap, setStaysMap] = useState<Map<string, Stay>>(new Map());
    const [popupIndex, setPopupIndex] = useState<number | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(false);
    const [mapViewMode, setMapViewMode] = useState<MapViewMode>("visits");
    const [isSatellite, setIsSatellite] = useState(false);
    const loadedUserIdRef = useRef<string | null>(null);
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY
    });

    const onLoad = useCallback((map: any) => {
        // Commented out to preserve state
        //const bounds = new window.google.maps.LatLngBounds(center);
        //map.fitBounds(bounds);
    }, []);

    const onUnmount = useCallback(() => {
        // Cleanup if needed
    }, []);

    const fetchLocations = useCallback(async (user: User, locations: Location[]) => {
        try {
            const visitsCollection = query(collection(db, 'visits'), where('userId', '==', user.id));
            const visitsCollectionSnapshot = await getDocs(visitsCollection);
            const newVisitsMap: Map<string, Visit> = new Map();
            let visitCount = 0;
            visitsCollectionSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data) {
                    newVisitsMap.set(data.locationId, data as Visit);
                    visitCount++;
                }
            });

            logDataLoaded('map visits', visitCount);

            const staysCollection = query(collection(db, 'stays'), where('userId', '==', user.id), where('stayed', '==', true));
            const staysCollectionSnapshot = await getDocs(staysCollection);
            const newStaysMap: Map<string, Stay> = new Map();
            staysCollectionSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data) {
                    newStaysMap.set(data.locationId, data as Stay);
                }
            });
            
            // Use pre-loaded locations passed in from props
            const newLocationsMap: Map<string, Location> = new Map();
            locations.forEach((location) => {
                const visit = newVisitsMap.get(location.id);
                // Show open pubs OR closed pubs that are visited (unless user wants to hide them)
                if(!location.pubIsClosed || (location.pubIsClosed && visit?.visited && !user.hideVisitedClosedPubsOnMap)) {
                    newLocationsMap.set(location.id, location);
                }
            });
            return { success: true, data: { locationsMap: newLocationsMap, visitsMap: newVisitsMap, staysMap: newStaysMap } };
        } catch (error) {
            console.log(error);
            return { success: false };
        }
    }, []);

    const loadData = useCallback(async(user: User) => {
        const res = await fetchLocations(user, props.locations);
        if (res.success && res.data) {
            setLocationsMap(res.data.locationsMap);
            setVisitsMap(res.data.visitsMap);
            setStaysMap(res.data.staysMap);
        }
    }, [fetchLocations, props.locations]);

    useEffect(() => {
        if (props.currentUser && props.currentUser.id !== loadedUserIdRef.current) {
            loadedUserIdRef.current = props.currentUser.id;
            loadData(props.currentUser);
        }
    }, [props.currentUser, loadData]);

    const getLocationSessions = useCallback((locationId: string): Session[] => {
        return props.sessions.filter((session: Session) => session.locationId === locationId);
    }, [props.sessions]);

    const getIconColor = useCallback((location: Location): string => {
        const activeSessions = getLocationSessions(location.id);
        if (mapViewMode === "stays") {
            if (staysMap.get(location.id)?.stayed && activeSessions.length) {
                return "/images/visited-marker-active2.png";
            } else if (staysMap.get(location.id)?.stayed) {
                return "/images/visited-marker.png";
            }
            return "/images/new-marker.png";
        }

        if (location.bonus && props.currentUser && !props.currentUser.socialDisabled) {
            return "/images/bonus-marker.png";
        }

        if (mapViewMode === "checkIns") {
            const checkedIn = !!visitsMap.get(location.id)?.verified;
            if (checkedIn && activeSessions.length) {
                return "/images/visited-marker-active2.png";
            } else if (checkedIn) {
                return "/images/visited-marker.png";
            } else if (activeSessions.length) {
                return "/images/new-marker-active2.png";
            }
            return "/images/new-marker.png";
        }

        if(location.visited && activeSessions.length) {
            return "/images/visited-marker-active2.png";
        } else if(location.visited) {
            return "/images/visited-marker.png";
        } else if(!location.visited && activeSessions.length) {
            return "/images/new-marker-active2.png";
        } else {
            return "/images/new-marker.png";
        }
    }, [props.currentUser, getLocationSessions, mapViewMode, staysMap, visitsMap]);

    const getMarkerPriority = useCallback((location: Location): number => {
        const activeSessions = getLocationSessions(location.id);
        if (mapViewMode === "stays") {
            return staysMap.get(location.id)?.stayed ? (activeSessions.length ? 2 : 1) : 0;
        }

        if (location.bonus && props.currentUser && !props.currentUser.socialDisabled) {
            return 5;
        }

        if (mapViewMode === "checkIns") {
            const checkedIn = !!visitsMap.get(location.id)?.verified;
            if (checkedIn && activeSessions.length) {
                return 4;
            } else if (!checkedIn && activeSessions.length) {
                return 3;
            } else if (checkedIn) {
                return 2;
            }
            return 1;
        }

        if(location.visited && activeSessions.length) {
            return 4;
        } else if(!location.visited && activeSessions.length) {
            return 3;
        } else if(location.visited) {
            return 2;
        }
        return 1;
    }, [props.currentUser, getLocationSessions, mapViewMode, staysMap, visitsMap]);

    const processLocations = useCallback((locations: Location[]): Location[] => {
        return locations.map((location) => {
            // Create a new object to avoid mutation
            const processedLocation = { ...location };
            // Set if location is visited
            if(props.currentUser && visitsMap) {
                const visitCheck = visitsMap.get(location.id);
                processedLocation.visited = !!(visitCheck && visitCheck.visited);
            }
            return processedLocation;
        }).filter(location => {
            if (mapViewMode === "stays" && !location.hotel) {
                return false;
            }
            // Show open pubs OR closed pubs that are visited (unless user wants to hide them)
            return !location.pubIsClosed || (location.pubIsClosed && location.visited && !props.currentUser?.hideVisitedClosedPubsOnMap);
        });
    }, [mapViewMode, props.currentUser, staysMap, visitsMap]);

    const addVisit = async (location: Location): Promise<void> => {
        if(!props.currentUser) {
            return;
        }
        const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
        try {
            const response = await toggleVisitedFunction({locationId: location.id});
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            await loadData(props.currentUser);
        } catch (error) {
            console.error('Error toggling visited:', error);
            alert('Failed to update visit status');
        }
    };

    const addStay = async (location: Location): Promise<void> => {
        if (!props.currentUser || !location.hotel) {
            return;
        }

        const toggleStayFunction = httpsCallable(functions, 'toggleStayV2');
        try {
            const response = await toggleStayFunction({locationId: location.id});
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            await loadData(props.currentUser);
        } catch (error) {
            console.error('Error toggling stay:', error);
            alert('Failed to update stay status');
        }
    };

    const displayVisitedButton = useCallback((location: Location): boolean => {
        // Don't show button if already visited
        if (visitsMap?.has(location.id) && visitsMap.get(location.id)?.visited) {
            return false;
        }
        // Don't show button if location hasn't opened yet
        if (location.openingDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const openingDate = new Date(location.openingDate);
            openingDate.setHours(0, 0, 0, 0);
            if (openingDate > today) {
                return false;
            }
        }
        return true;
    }, [visitsMap]);

    const displayMapActionButton = useCallback((location: Location): boolean => {
        if (mapViewMode === "stays") {
            return location.hotel && !staysMap.get(location.id)?.stayed;
        }
        return displayVisitedButton(location);
    }, [displayVisitedButton, mapViewMode, staysMap]);

    // Memoize processed locations to prevent recalculation on marker clicks
    const processedLocations = useMemo(() => {
        const locations = processLocations(Array.from(locationsMap.values()));
        return locations.sort((a, b) => {
            const nameComparison = a.name.localeCompare(b.name);
            return nameComparison !== 0 ? nameComparison : a.id.localeCompare(b.id);
        });
    }, [locationsMap, processLocations]);

    // Prevent mobile zoom gestures on this page
    usePreventMobileZoom();

    if (!isLoaded) return null;

    const mapModeControls = (
        <div className="map-v2-mode-controls">
            <button
                className={`cursor-pointer map-v2-mode-button${mapViewMode === "visits" ? " active" : ""}`}
                aria-label="Show visits"
                aria-pressed={mapViewMode === "visits"}
                onClick={() => setMapViewMode("visits")}
                title="Show visits"
                type="button"
            >
                <FontAwesomeIcon icon={faCheckCircle} /> Visits
            </button>
            <button
                className={`cursor-pointer map-v2-mode-button${mapViewMode === "checkIns" ? " active" : ""}`}
                aria-label="Show check-ins"
                aria-pressed={mapViewMode === "checkIns"}
                onClick={() => setMapViewMode("checkIns")}
                title="Show check-ins"
                type="button"
            >
                <FontAwesomeIcon icon={faLocationDot} /> Check-ins
            </button>
            <button
                className={`cursor-pointer map-v2-mode-button${mapViewMode === "stays" ? " active" : ""}`}
                aria-label="Show stays"
                aria-pressed={mapViewMode === "stays"}
                onClick={() => setMapViewMode("stays")}
                title="Show stays"
                type="button"
            >
                <FontAwesomeIcon icon={faBed} /> Stays
            </button>
        </div>
    );

    const map = (
        <div className={isUiV2 ? "map-v2-map-wrap" : undefined}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={5.4}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    mapTypeControl: !isUiV2,
                    mapTypeId: isUiV2 ? (isSatellite ? "satellite" : "roadmap") : undefined,
                    streetViewControl: false,
                }}
            >
                {props.locations?.length > 0 && visitsMap && processedLocations.map((location: Location, index) => (
                    <Marker
                        key={location.id}
                        icon={getIconColor(location)}
                        zIndex={(getMarkerPriority(location) * (processedLocations.length + 1)) + (processedLocations.length - index)}
                        position={{ lat: +location.lat, lng: +location.lng }}
                        onClick={() => { setPopupIndex(index); setIsOpen(true) }}
                        title={location.name}
                    >
                        {isOpen && popupIndex === index && (
                            <InfoWindow
                                onCloseClick={() => setIsOpen(false)}
                            >
                                <div className="info-window">
                                    <div className="info-header">
                                        <Link to={`/location/${location.id}/comments`}>{location.name}</Link>
                                    </div>
                                    <div className="info-address">
                                        <Link
                                            to={getGoogleMapsLink(location.name + ', ' + location.city)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {location.address}
                                        </Link>
                                    </div>
                                    <div className="info-address">
                                        <div className="mt-1">
                                            Visit count: <strong>{location.visitCount}</strong>
                                        </div>
                                    </div>
                                    {getLocationSessions(location.id).length > 0 && (
                                        <>
                                            <div className="mt-1">
                                                <span>Users currently checked in: <b>{getLocationSessions(location.id).length}</b></span>
                                            </div>
                                            <div className="mt-1">
                                                {getLocationSessions(location.id).map((session) => (
                                                    <div key={session.id}>
                                                        <Link to={`/profile/${session.userId}/visits`}>{session.name}</Link>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <div className={displayMapActionButton(location) ? "record-visit d-flex justify-content-end" : "record-visit d-none"}>
                                        <Button size="sm" onClick={() => mapViewMode === "stays" ? addStay(location) : addVisit(location)}>
                                            {mapViewMode === "stays" ? "Record Stay" : "Record Visit"}
                                        </Button>
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                ))}
            </GoogleMap>
            {isUiV2 && (
                <button
                    aria-label={isSatellite ? "Show map view" : "Show satellite view"}
                    aria-pressed={isSatellite}
                    className={`cursor-pointer v2-map-type-button${isSatellite ? " active" : ""}`}
                    onClick={() => setIsSatellite((satellite) => !satellite)}
                    title={isSatellite ? "Show map view" : "Show satellite view"}
                    type="button"
                >
                    <FontAwesomeIcon icon={isSatellite ? faLayerGroup : faSatelliteDish} />
                </button>
            )}
            {isUiV2 && (
                <div className="map-v2-mode-overlay">
                    {mapModeControls}
                </div>
            )}
        </div>
    );

    return (
        <>
            <PageTracking />
            <AuthCheck />
            {props.locations && props.locations.length > 0 && (
                isUiV2 ? (
                    <div className="map-v2-page">
                        {map}
                    </div>
                ) : (
                    <div style={{width: "100%", height: "80%"}}>
                        <div className="map-view-header d-flex justify-content-end align-items-center" style={{marginTop: "-18px"}}>
                            <ButtonGroup size="sm" className="mt-0 map-view-toggle" style={{fontSize: "0.5rem"}}>
                                <Button
                                    variant={mapViewMode === "visits" ? "primary" : "outline-primary"}
                                    onClick={() => setMapViewMode("visits")}
                                    style={{fontSize: "0.6rem"}}
                                >
                                    Visits
                                </Button>
                                <Button
                                    variant={mapViewMode === "checkIns" ? "primary" : "outline-primary"}
                                    onClick={() => setMapViewMode("checkIns")}
                                    style={{fontSize: "0.6rem"}}
                                >
                                    Check-ins
                                </Button>
                            </ButtonGroup>
                        </div>
                        {map}
                    </div>
                )
            )}
        </>
    );
};

export default React.memo(MapElement);
